/**
 * Planner UI primitives. Every control in components/planner is built from
 * these so the panel has one visual system:
 *
 *   Button     primary · filled · secondary · ghost · danger   (sm 32 · md 40 · lg 44px)
 *   IconButton square ghost button with a required aria-label
 *   Chip       small selectable/actionable token (filters)
 *   Pill       read-only status badge
 *   SectionLabel  12px sentence-case caption over a group
 *   Menu       trigger + labelled groups of actions
 *   Sheet      enter transition for anything that appears inside the panel
 *   FIELD_*    the one input look (40px, 10px radius, leading icon slot)
 *
 * Rules the variants encode:
 *  • One primary per view. It is the thing the panel exists to do next.
 *  • Secondary is outlined; ghost is text-only; danger is ghost in red and is
 *    only for actions that throw work away (they still offer Undo).
 *  • Every button carries data-variant so tests and audits can count tiers.
 *
 * Tailwind preflight is off in this app (the Shopify theme ships its own
 * reset), so borders, backgrounds and min-heights are set explicitly.
 */
import React from 'react';
import { createPortal } from 'react-dom';

/**
 * Was the last thing the user did a key press (not a tap or a click)?
 * Dialogs move focus to their first button only for keyboard users: a ring
 * appearing on a button you never touched reads as a glitch on a phone.
 */
let lastInputKeyboard = false;
if (typeof window !== 'undefined') {
  window.addEventListener('keydown', (e) => { if (!e.metaKey && !e.ctrlKey && !e.altKey) lastInputKeyboard = true; }, true);
  window.addEventListener('pointerdown', () => { lastInputKeyboard = false; }, true);
}
export function lastInputWasKeyboard(): boolean {
  return lastInputKeyboard;
}

/**
 * While a modal sheet (menu, confirm, share) is up, <html data-tp-modal>:
 * the trip sheet's own grab handle hides under it, so a phone never shows
 * two handles stacked 60px apart. Counted, so nested modals don't clear it early.
 */
let modalCount = 0;
export function useModalFlag(active: boolean) {
  React.useEffect(() => {
    if (!active || typeof document === 'undefined') return;
    modalCount += 1;
    document.documentElement.setAttribute('data-tp-modal', '');
    return () => {
      modalCount = Math.max(0, modalCount - 1);
      if (!modalCount) document.documentElement.removeAttribute('data-tp-modal');
    };
  }, [active]);
}

export type ButtonVariant = 'primary' | 'filled' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

const BASE =
  'inline-flex min-h-0 select-none items-center justify-center gap-1.5 whitespace-nowrap rounded-tp font-semibold ' +
  'transition-[background-color,border-color,color,box-shadow,transform] duration-150 ease-tp-out ' +
  // One 2px ring, flush to the edge (no offset gap: a filled button with a
  // ring floating off it read as a double outline). Keyboard only: the
  // site-wide `*:focus` outline is switched off here, so a tap or a dialog
  // opening never draws a ring.
  'focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/50 ' +
  'active:translate-y-px disabled:cursor-not-allowed disabled:active:translate-y-0';

// Disabled states are drawn, not just faded: a flat grey fill and grey text,
// so "can't do this yet" never looks like a lighter version of "do this".
const VARIANT: Record<ButtonVariant, string> = {
  primary:
    'border border-solid border-tp-brand bg-tp-brand text-white shadow-[0_1px_2px_rgba(21,88,176,0.35)] hover:border-tp-brand-ink hover:bg-tp-brand-ink ' +
    'disabled:border-tp-line disabled:bg-tp-surface disabled:text-tp-faint disabled:shadow-none',
  // Filled secondary: a solid navy action inside a hint or card (Apply).
  // Firmer than outlined, never the brand blue, so the one primary stays one.
  filled:
    'border border-solid border-tp-ink bg-tp-ink text-white hover:border-[#0b1424] hover:bg-[#0b1424] ' +
    'disabled:border-tp-line disabled:bg-tp-surface disabled:text-tp-faint',
  secondary:
    'border border-solid border-tp-line-strong bg-white text-tp-ink hover:border-tp-muted/50 hover:bg-tp-surface ' +
    'disabled:border-tp-line disabled:bg-white disabled:text-tp-faint',
  ghost:
    'border border-solid border-transparent bg-transparent text-tp-muted hover:bg-tp-surface hover:text-tp-ink disabled:text-tp-line-strong disabled:hover:bg-transparent',
  danger:
    'border border-solid border-transparent bg-transparent text-tp-muted hover:bg-tp-danger-soft hover:text-tp-danger disabled:text-tp-line-strong',
};

// sm 32px (inline, desktop rows) · md 40px (footer, fields) · lg 44px (touch).
const SIZE: Record<ButtonSize, string> = {
  sm: 'h-8 px-2.5 text-tp-sm',
  md: 'h-10 px-3.5 text-tp-base',
  lg: 'h-11 px-4 text-tp-base',
};

export function buttonClasses(variant: ButtonVariant = 'secondary', size: ButtonSize = 'sm', extra = ''): string {
  return [BASE, VARIANT[variant], SIZE[size], extra].filter(Boolean).join(' ');
}

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: React.ReactNode;
  /** Pressed/armed state for toggle buttons (sets aria-pressed). */
  pressed?: boolean;
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'sm', icon, pressed, className = '', children, type = 'button', ...rest },
  ref
) {
  return (
    <button
      ref={ref}
      type={type}
      data-variant={variant}
      aria-pressed={pressed === undefined ? undefined : pressed}
      className={buttonClasses(variant, size, className)}
      {...rest}
    >
      {icon}
      {children}
    </button>
  );
});

type IconButtonProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label'> & {
  label: string;
  children: React.ReactNode;
  /** sm 32px · md 40px · lg 44px square. */
  size?: ButtonSize;
};

const ICON_SQUARE: Record<ButtonSize, string> = { sm: 'w-8', md: 'w-10', lg: 'w-11' };

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, className = '', children, type = 'button', size = 'sm', ...rest },
  ref
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={rest.title ?? label}
      data-variant="ghost"
      className={buttonClasses('ghost', size, `${ICON_SQUARE[size]} px-0 ${className}`)}
      {...rest}
    >
      {children}
    </button>
  );
});

type ChipProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  selected?: boolean;
  icon?: React.ReactNode;
};

export function Chip({ selected, icon, className = '', children, type = 'button', ...rest }: ChipProps) {
  return (
    <button
      type={type}
      data-variant="chip"
      aria-pressed={selected === undefined ? undefined : selected}
      className={[
        'inline-flex h-8 min-h-0 items-center gap-1 whitespace-nowrap rounded-full border border-solid px-3 text-tp-sm font-medium',
        'transition-colors duration-150 ease-tp-out focus:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45',
        'disabled:cursor-not-allowed disabled:opacity-50',
        selected
          ? 'border-tp-brand bg-tp-brand-soft text-tp-brand'
          : 'border-tp-line bg-white text-tp-muted hover:border-tp-line-strong hover:text-tp-ink',
        className,
      ].join(' ')}
      {...rest}
    >
      {icon}
      {children}
    </button>
  );
}

export type PillTone = 'brand' | 'neutral' | 'night' | 'warn' | 'ok';

const PILL_TONE: Record<PillTone, string> = {
  brand: 'bg-tp-brand text-white',
  neutral: 'bg-tp-surface text-tp-muted ring-1 ring-inset ring-tp-line',
  night: 'bg-tp-night-soft text-tp-night',
  warn: 'bg-tp-warn-soft text-tp-warn',
  ok: 'bg-tp-ok-soft text-tp-ok',
};

export function Pill({
  tone = 'neutral',
  className = '',
  children,
  ...rest
}: React.HTMLAttributes<HTMLSpanElement> & { tone?: PillTone }) {
  return (
    <span
      className={`inline-flex h-6 min-w-[24px] items-center justify-center gap-1 rounded-full px-2 text-tp-xs font-semibold tabular-nums ${PILL_TONE[tone]} ${className}`}
      {...rest}
    >
      {children}
    </span>
  );
}

export function SectionLabel({
  as: Tag = 'p',
  className = '',
  children,
  ...rest
}: React.HTMLAttributes<HTMLElement> & { as?: 'p' | 'h3' | 'h4' | 'span' }) {
  return (
    <Tag className={`m-0 text-tp-xs font-semibold text-tp-muted ${className}`} {...rest}>
      {children}
    </Tag>
  );
}

/**
 * Sheet — wraps content that appears inside the panel (a mode, a banner, a
 * picker) so it eases in instead of snapping. Keyed remounts replay it.
 * Honours prefers-reduced-motion.
 */
export function Sheet({
  className = '',
  children,
  speed = 'enter',
  ...rest
}: React.HTMLAttributes<HTMLDivElement> & { speed?: 'enter' | 'fade' | 'sheet' }) {
  const anim = speed === 'fade' ? 'animate-tp-fade' : speed === 'sheet' ? 'animate-tp-sheet' : 'animate-tp-enter';
  return (
    <div className={`${anim} motion-reduce:animate-none ${className}`} {...rest}>
      {children}
    </div>
  );
}

export interface MenuItem {
  key: string;
  label: string;
  hint?: string;
  icon?: React.ReactNode;
  tone?: 'default' | 'danger';
  /** Draw a divider above this item. */
  separated?: boolean;
  /** Small heading above this item (starts a labelled group). */
  group?: string;
  disabled?: boolean;
  onSelect: () => void;
  title?: string;
  /** Small trailing note at the row's end. */
  trailing?: React.ReactNode;
  /** A glyph right after the label, on its line (the guests' lock on Export). */
  badge?: React.ReactNode;
}

/**
 * Menu — a trigger that opens a small list of actions, in labelled groups.
 * The list is portalled to <body> with fixed coordinates, so a menu on a row
 * near the edge of a scrolling list is never clipped by it.
 * `placement="auto"` opens upward when there isn't room below (rows near the
 * bottom of the list, the footer). Keyboard: Enter/Space/↓ opens and focuses
 * the first enabled item, ↑/↓ move (skipping disabled items), Esc closes and
 * returns focus, Tab, a click outside or scrolling the page closes.
 */
export function Menu({
  label,
  ariaLabel,
  icon,
  items,
  labelClassName = '',
  placement = 'top',
  align = 'end',
  className = '',
  variant = 'ghost',
  size = 'sm',
  triggerClassName = '',
  iconOnly = false,
  testId,
  sheetOnMobile = false,
  sheetTitle,
  sheetSubtitle,
  scrim = false,
  maxPopoverHeight = 440,
}: {
  label: string;
  /** Fuller accessible name; must start with the visible label (or stand alone when iconOnly). */
  ariaLabel?: string;
  /** e.g. 'hidden sm:inline' to collapse to the icon on narrow screens. */
  labelClassName?: string;
  icon?: React.ReactNode;
  items: MenuItem[];
  placement?: 'top' | 'bottom' | 'auto';
  align?: 'start' | 'end';
  className?: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  triggerClassName?: string;
  iconOnly?: boolean;
  testId?: string;
  /** Under 768px, open as a bottom action sheet instead of a popover. */
  sheetOnMobile?: boolean;
  /** Heading shown at the top of the action sheet. */
  sheetTitle?: string;
  /** A muted line under the sheet's heading (the trip's name). */
  sheetSubtitle?: string;
  /** Popover too: dim the page behind it, so the list reads as on top. */
  scrim?: boolean;
  /** Popover only: the tallest it gets before it scrolls (px). */
  maxPopoverHeight?: number;
}) {
  const [open, setOpen] = React.useState(false);
  const [asSheet, setAsSheet] = React.useState(false);
  const [pos, setPos] = React.useState<React.CSSProperties>({});
  // Popover: where its little arrow sits (px from the list's left edge) and
  // which edge it hangs from, so the list visibly belongs to its trigger.
  const [arrow, setArrow] = React.useState<{ x: number; up: boolean } | null>(null);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const itemRefs = React.useRef<(HTMLButtonElement | null)[]>([]);
  const menuId = React.useId();

  React.useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      const t = e.target as Node;
      if (rootRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    // The list is fixed to the trigger: when something scrolls, it follows
    // the trigger, and closes once the trigger has left the screen.
    const onScroll = (e: Event) => {
      if (menuRef.current && e.target instanceof Node && menuRef.current.contains(e.target)) return;
      const r = triggerRef.current?.getBoundingClientRect();
      if (!r || r.bottom < 0 || r.top > window.innerHeight) {
        setOpen(false);
        return;
      }
      if (!asSheetRef.current) setPos((prev) => placeMenu(r, prev.bottom !== undefined));
    };
    const onResize = () => setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    // Keyboard: focus the first item. Tap or click: focus the list itself, so
    // no row lights up that you didn't point at (arrows still work from there).
    if (lastInputWasKeyboard()) itemRefs.current.find((el) => el && !el.disabled)?.focus({ preventScroll: true });
    else menuRef.current?.focus({ preventScroll: true });
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);

  const asSheetRef = React.useRef(false);
  asSheetRef.current = asSheet;
  useModalFlag(open && asSheet);

  const placeMenu = (r: DOMRect, up: boolean): React.CSSProperties => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const width = Math.min(288, vw - 16);
    let left = align === 'end' ? r.right - width : r.left;
    left = Math.max(8, Math.min(left, vw - width - 8));
    const ax = Math.max(14, Math.min(width - 14, r.left + r.width / 2 - left));
    setArrow((prev) => (prev && prev.x === ax && prev.up === up ? prev : { x: ax, up }));
    return {
      position: 'fixed',
      left,
      width,
      ...(up ? { bottom: vh - r.top + 10 } : { top: r.bottom + 10 }),
      // Capped, so a long menu never buries the trip it belongs to: the
      // name and the figures above stay in view.
      maxHeight: Math.min(maxPopoverHeight, Math.max(160, (up ? r.top : vh - r.bottom) - 16)),
    };
  };

  const toggle = () => {
    if (!open && triggerRef.current && typeof window !== 'undefined') {
      const sheetMode = sheetOnMobile && window.innerWidth < 768;
      setAsSheet(sheetMode);
      const r = triggerRef.current.getBoundingClientRect();
      const estimate = Math.min(420, 24 + items.length * 46);
      const below = window.innerHeight - r.bottom;
      const up = placement === 'top' ? true : placement === 'bottom' ? false : below < estimate && r.top > below;
      setPos(placeMenu(r, up));
    }
    setOpen((v) => !v);
  };

  const close = (refocus = true) => {
    setOpen(false);
    if (refocus) triggerRef.current?.focus();
  };

  const onMenuKey = (e: React.KeyboardEvent) => {
    const els = (itemRefs.current.filter(Boolean) as HTMLButtonElement[]).filter((el) => !el.disabled);
    const i = els.indexOf(document.activeElement as HTMLButtonElement);
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      close();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      els[(i + 1) % els.length]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      els[(i - 1 + els.length) % els.length]?.focus();
    } else if (e.key === 'Tab') {
      setOpen(false);
    }
  };

  return (
    <div ref={rootRef} className={`relative ${className}`} data-testid={testId}>
      <Button
        ref={triggerRef}
        variant={variant}
        size={size}
        icon={icon}
        aria-label={ariaLabel ?? (iconOnly ? label : undefined)}
        title={iconOnly ? ariaLabel ?? label : undefined}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            if (!open) toggle();
          }
        }}
        className={`${open ? '!bg-tp-surface !text-tp-ink' : ''} ${triggerClassName}`}
      >
        {!iconOnly && <span className={labelClassName}>{label}</span>}
      </Button>
      {open && typeof document !== 'undefined' && createPortal(
        <>
        {(asSheet || scrim) && (
          <div
            aria-hidden="true"
            onClick={() => close(false)}
            className={`fixed inset-0 z-[69] animate-tp-fade motion-reduce:animate-none ${asSheet ? 'bg-[#16233a]/40' : 'bg-[#16233a]/20'}`}
            data-testid="menu-scrim"
          />
        )}
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label={ariaLabel ?? label}
          tabIndex={-1}
          onKeyDown={onMenuKey}
          style={asSheet ? undefined : pos}
          data-presentation={asSheet ? 'sheet' : 'popover'}
          className={
            asSheet
              ? 'fixed inset-x-0 bottom-0 z-[70] box-border max-h-[80vh] overflow-y-auto rounded-t-[18px] bg-white px-2 pb-[max(8px,env(safe-area-inset-bottom))] pt-2 shadow-tp-pop outline-none animate-tp-sheet motion-reduce:animate-none'
              : // An elevated card: 12px radius, a deep soft shadow, so it
                // reads as floating over the panel, not part of it.
                'z-[70] box-border flex flex-col rounded-[12px] border border-solid border-tp-line bg-white shadow-[0_18px_40px_-12px_rgba(22,35,58,0.35),0_4px_10px_-4px_rgba(22,35,58,0.12)] outline-none animate-tp-enter motion-reduce:animate-none'
          }
        >
          {!asSheet && arrow && (
            // The arrow points at the trigger: the list hangs off "⋯".
            <span
              aria-hidden="true"
              className={`pointer-events-none absolute h-3.5 w-3.5 rotate-45 border-solid border-tp-line bg-white ${
                arrow.up
                  ? '-bottom-[7.5px] border-0 border-b border-r shadow-[3px_3px_6px_-2px_rgba(22,35,58,0.18)]'
                  : '-top-[7.5px] border-0 border-l border-t'
              }`}
              // -1: the list's 1px border shifts its absolute children.
              style={{ left: arrow.x - 8 }}
            />
          )}
          {asSheet && <span aria-hidden="true" className="mx-auto mb-1 block h-1 w-9 rounded-full bg-tp-line-strong" />}
          {/* The same heading on the phone sheet and the desktop popover:
              which trip these actions are for. */}
          {(sheetTitle || sheetSubtitle) && (
            <div
              className={asSheet ? 'px-2.5 pb-1.5 pt-2' : 'flex-none border-0 border-b border-solid border-tp-line px-4 pb-2.5 pt-3'}
              aria-hidden="true"
              data-testid="menu-heading"
            >
              {sheetTitle && (
                <p className={`m-0 truncate font-semibold text-tp-ink ${asSheet ? 'text-[17px] leading-6' : 'text-tp-base'}`}>{sheetTitle}</p>
              )}
              {sheetSubtitle && <p className={`m-0 truncate text-tp-muted ${asSheet ? 'text-tp-sm' : 'text-tp-xs'}`}>{sheetSubtitle}</p>}
            </div>
          )}
          <div className={asSheet ? '' : 'min-h-0 flex-1 overflow-y-auto overscroll-contain p-1.5'}>
          {items.map((item, idx) => (
            <React.Fragment key={item.key}>
              {(item.separated || (item.group && idx > 0)) && (
                <div role="separator" className="mx-2 my-1 h-px bg-tp-line" />
              )}
              {item.group && (
                <p className="m-0 px-2.5 pb-1 pt-2 text-[12px] font-semibold leading-4 tracking-[0.01em] text-tp-muted" aria-hidden="true">
                  {item.group}
                </p>
              )}
              <button
                ref={(el) => {
                  itemRefs.current[idx] = el;
                }}
                type="button"
                role="menuitem"
                title={item.title}
                disabled={item.disabled}
                aria-disabled={item.disabled || undefined}
                onClick={() => {
                  if (item.disabled) return;
                  close(false);
                  item.onSelect();
                }}
                className={`flex ${asSheet ? 'min-h-[48px]' : 'min-h-[40px]'} w-full items-center gap-2.5 rounded-tp-sm border-0 bg-transparent px-2.5 py-2 text-left text-tp-base outline-none transition-colors duration-150 ease-tp-out focus:outline-none disabled:cursor-not-allowed disabled:text-tp-faint disabled:hover:bg-transparent ${
                  item.tone === 'danger'
                    ? 'text-tp-danger hover:bg-tp-danger-soft focus-visible:bg-tp-danger-soft'
                    : 'text-tp-ink hover:bg-tp-surface focus-visible:bg-tp-surface'
                }`}
              >
                {item.icon && (
                  <span className={`flex-none text-current ${item.disabled ? 'opacity-40' : 'opacity-80'}`}>{item.icon}</span>
                )}
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5 font-medium">
                    {item.label}
                    {item.badge}
                  </span>
                  {item.hint && (
                    <span className={`block text-tp-xs ${item.disabled ? 'text-tp-faint' : 'text-tp-muted'}`}>{item.hint}</span>
                  )}
                </span>
                {item.trailing && <span className="flex flex-none items-center self-center">{item.trailing}</span>}
              </button>
            </React.Fragment>
          ))}
          </div>
          {/* Phones: a quiet way out, lighter than any row (a tap on the
              dimmed page closes it too). */}
          {asSheet && (
            <button
              type="button"
              onClick={() => close()}
              className="mt-1 flex h-11 min-h-0 w-full items-center justify-center rounded-tp border-0 bg-transparent text-tp-base font-semibold text-tp-muted transition-colors duration-150 ease-tp-out hover:text-tp-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45"
              data-testid="menu-cancel"
            >
              Cancel
            </button>
          )}
        </div>
        </>,
        document.body
      )}
    </div>
  );
}

/**
 * The planner's one input look: 40px tall, 10px radius, leading icon slot,
 * brand focus ring. Height/radius go inline because the storefront theme CSS
 * squares off bare inputs with a more specific selector than a utility class.
 */
export const FIELD_CLASS =
  'box-border block w-full rounded-tp border border-solid border-tp-line-strong bg-white text-tp-base text-tp-ink ' +
  'placeholder:text-tp-faint transition-[border-color,box-shadow] duration-150 ease-tp-out ' +
  'hover:border-tp-muted/60 focus:border-tp-brand focus:outline-none focus:ring-2 focus:ring-tp-brand/20';

export const FIELD_STYLE: React.CSSProperties = { height: 40, borderRadius: 10, margin: 0 };

// Layout effects measure before paint in the browser; on the server (and
// in static-markup tests) there is nothing to measure.
const useIsoLayoutEffect = typeof window !== 'undefined' ? React.useLayoutEffect : React.useEffect;

/**
 * Dialog — a modal for one decision or one result (confirm "New trip", the
 * share link). Centred card on desktop, a bottom sheet on phones. Portalled
 * to <body>; Esc and the backdrop close it; focus moves in on open (the
 * `initialFocus` element, else the first button), Tab stays inside, and
 * focus returns to whatever opened it on close.
 */
export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  initialFocusRef,
  testId,
  role = 'dialog',
  hideClose = false,
  anchorRef,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: React.ReactNode;
  children?: React.ReactNode;
  initialFocusRef?: React.RefObject<HTMLElement>;
  testId?: string;
  /** 'alertdialog' for a confirm that throws work away. */
  role?: 'dialog' | 'alertdialog';
  /** No ✕: the dialog's own buttons already say how out (a confirm's "Keep planning"). */
  hideClose?: boolean;
  /**
   * Desktop: rise from this element instead of the middle of the screen.
   * The panel's bottom-right corner lands on the element's, so a sheet
   * opened from the action bar stays where your eyes and pointer are.
   * Phones keep the bottom sheet.
   */
  anchorRef?: React.RefObject<HTMLElement>;
}) {
  const panelRef = React.useRef<HTMLDivElement>(null);
  const [anchorBox, setAnchorBox] = React.useState<React.CSSProperties | null>(null);
  useIsoLayoutEffect(() => {
    if (!open || !anchorRef?.current || typeof window === 'undefined') {
      setAnchorBox(null);
      return;
    }
    const place = () => {
      const el = anchorRef.current;
      if (!el || window.innerWidth < 768) return setAnchorBox(null);
      const r = el.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const right = Math.max(12, vw - r.right);
      const bottom = Math.max(12, vh - r.bottom);
      setAnchorBox({
        position: 'fixed',
        right,
        bottom,
        width: Math.min(440, vw - right - 12),
        maxHeight: vh - bottom - 12,
        overflowY: 'auto',
      });
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [open, anchorRef]);
  const titleId = React.useId();
  const descId = React.useId();
  const onCloseRef = React.useRef(onClose);
  onCloseRef.current = onClose;
  useModalFlag(open);

  React.useEffect(() => {
    if (!open) return;
    const opener = document.activeElement as HTMLElement | null;
    // Keyboard users land on the default button. A tap or a click focuses
    // the dialog itself: focus is trapped inside, but no ring appears on a
    // button nobody touched, and Tab still starts from the top.
    const keyboard = lastInputWasKeyboard();
    const t = window.setTimeout(() => {
      const target = keyboard
        ? initialFocusRef?.current ??
          panelRef.current?.querySelector<HTMLElement>('button, [href], input, [tabindex]:not([tabindex="-1"])')
        : panelRef.current;
      target?.focus({ preventScroll: true });
    }, 0);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const els = Array.from(
        panelRef.current.querySelectorAll<HTMLElement>('button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])')
      );
      if (!els.length) return;
      const first = els[0];
      const last = els[els.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener('keydown', onKey, true);
      if (opener && document.contains(opener)) opener.focus({ preventScroll: true });
    };
  }, [open, initialFocusRef]);

  if (!open || typeof document === 'undefined') return null;
  return createPortal(
    <div className={`fixed inset-0 z-[80] flex items-end justify-center ${anchorBox ? '' : 'md:items-center md:p-6'}`}>
      <div
        aria-hidden="true"
        onClick={onClose}
        className={`absolute inset-0 animate-tp-fade motion-reduce:animate-none ${anchorBox ? 'bg-[#16233a]/25' : 'bg-[#16233a]/45'}`}
      />
      <div
        ref={panelRef}
        role={role}
        tabIndex={-1}
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
        data-testid={testId}
        {...(anchorBox ? { 'data-anchored': '' } : {})}
        style={anchorBox ?? undefined}
        className={`box-border w-full max-w-full rounded-t-[18px] bg-white outline-none px-5 pb-[max(20px,env(safe-area-inset-bottom))] pt-3 shadow-tp-pop animate-tp-sheet motion-reduce:animate-none md:rounded-tp-lg md:px-6 md:pb-6 md:pt-6 md:animate-tp-enter ${
          anchorBox ? '[transform-origin:100%_100%] md:shadow-[0_24px_56px_-16px_rgba(22,35,58,0.45),0_6px_16px_-6px_rgba(22,35,58,0.18)]' : 'relative md:w-[440px]'
        }`}
      >
        <span aria-hidden="true" className="mx-auto mb-3 block h-1 w-9 rounded-full bg-tp-line-strong md:hidden" />
        <h2 id={titleId} className={`m-0 ${hideClose ? '' : 'pr-8'} text-[18px] font-semibold leading-6 tracking-[-0.01em] text-tp-ink`}>
          {title}
        </h2>
        {description && (
          <div id={descId} className="mt-1.5 text-tp-base leading-[21px] text-tp-muted">
            {description}
          </div>
        )}
        {!hideClose && (
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          // A 36px target, a 20px slate-600 glyph: easy to see, easy to hit.
          className="absolute right-3 top-3 flex h-9 w-9 min-h-0 items-center justify-center rounded-tp-sm border-0 bg-transparent text-[#475569] transition-colors duration-150 hover:bg-tp-surface hover:text-tp-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45 max-md:top-4"
          data-testid="dialog-close"
        >
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
        )}
        {children}
      </div>
    </div>,
    document.body
  );
}
